/**
 * Dashboard Page
 * Overview page with welcome message, quick stats, agents needing attention, and root cause analysis
 */

import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { BarChart3, TrendingUp, UserCheck, GraduationCap, MessageSquare } from 'lucide-react';
import { getBrandName } from '@shared/config/brand';
import MetricCard from '../components/dashboard/MetricCard';
import TrendChart from '../components/dashboard/TrendChart';
import DistributionChart from '../components/dashboard/DistributionChart';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import Spinner from '../components/shared/Spinner';
import EmptyState from '../components/shared/EmptyState';
import RefreshButton from '../components/shared/RefreshButton';
import useDateRange from '../hooks/useDateRange';
import { getAgentLeaderboard, getOverviewDashboard } from '../services/analyticsService';
import { getDashboardSSOT, getContactReasons } from '../services/sentimentService';
import { getQASummary } from '../services/qaService';
import { toTitleCase, sentimentToPercent, qaToPercent } from '../utils/formatters';

const DISPOSITION_COLORS = {
  resolved: '#22c55e',
  escalated: '#ef4444',
  transferred: '#3b82f6',
  abandoned: '#6b7280',
  follow_up_required: '#f59e0b',
  pending: '#9ca3af',
};

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [ssot, setSsot] = useState(null);
  const [qaData, setQaData] = useState(null);
  const [contactReasons, setContactReasons] = useState([]);
  const [agents, setAgents] = useState([]);
  const [overview, setOverview] = useState(null);
  const { from, to, onChange: onDateChange } = useDateRange();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const sentimentRange = { from, to };
      const [ssotRes, qaRes, reasonsRes, agentsRes, overviewRes] = await Promise.all([
        getDashboardSSOT(sentimentRange),
        getQASummary(sentimentRange),
        getContactReasons(sentimentRange),
        getAgentLeaderboard({ from_date: from, to_date: to, page_size: 10 }),
        getOverviewDashboard({ from_date: from, to_date: to }),
      ]);
      if (ssotRes.success) setSsot(ssotRes.data);
      else toast.error(ssotRes.error);
      if (qaRes.success) setQaData(qaRes.data);
      if (reasonsRes.success) setContactReasons(reasonsRes.data);
      if (agentsRes.success) setAgents(agentsRes.data);
      if (overviewRes.success) setOverview(overviewRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Spinner />;
  if (!ssot) return <EmptyState title="No analytics data yet" description="Dashboard metrics appear once conversations are processed through the pipeline." />;

  const trendData = ssot.daily_trends || [];
  const dispositions = ssot.disposition_breakdown || [];
  const avgQaScore = qaData?.avg_total_score_100;

  // Bottom 3 agents by sentiment for "needs attention" list
  const attentionAgents = [...agents]
    .sort((a, b) => (a.avg_sentiment || 0) - (b.avg_sentiment || 0))
    .slice(0, 3)
    .filter(a => a.avg_sentiment != null);

  return (
    <div>
      {/* Welcome Banner */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <BarChart3 size={24} className="text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Welcome to {getBrandName()} Analytics</h2>
              <p className="text-sm text-gray-500">Monitor sentiment, quality, and agent performance in real time.</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <DateRangeFilter from={from} to={to} onChange={onDateChange} showPresets />
            <RefreshButton onClick={fetchData} loading={loading} />
          </div>
        </div>
      </div>

      {/* Quick Stats — 6 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <MetricCard
          label="Total Conversations"
          value={ssot.total_conversations}
          subtext={overview?.excluded_count ? `Excluded: ${overview.excluded_count}` : undefined}
        />
        <MetricCard
          label="Avg Sentiment"
          value={sentimentToPercent(ssot.avg_sentiment_score) + '%'}
          color="#22c55e"
        />
        <MetricCard
          label="Active Agents"
          value={ssot.active_agents}
          color="#6366f1"
        />
        <MetricCard
          label="Avg Handle Time"
          value={ssot.avg_handle_time != null ? `${Math.round(ssot.avg_handle_time)}s` : '-'}
          color="#3b82f6"
        />
        <MetricCard
          label="Avg QA Score"
          value={avgQaScore != null ? `${Math.round(avgQaScore)}%` : '-'}
          color="#8b5cf6"
        />
        <MetricCard
          label="Resolution Rate"
          value={ssot.resolution_rate != null ? `${ssot.resolution_rate}%` : '-'}
          color="#f59e0b"
        />
      </div>

      {/* Quick Links + Agents Needing Attention */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <Link to="/sentiment" className="card-hover p-5 block">
          <div className="flex items-center space-x-3 mb-2">
            <TrendingUp size={20} className="text-primary-600" />
            <h3 className="font-semibold text-gray-900">Sentiment Trends</h3>
          </div>
          <p className="text-sm text-gray-500">View customer sentiment signals and trends across all channels.</p>
        </Link>
        <Link to="/conversations" className="card-hover p-5 block">
          <div className="flex items-center space-x-3 mb-2">
            <MessageSquare size={20} className="text-primary-600" />
            <h3 className="font-semibold text-gray-900">Conversations</h3>
          </div>
          <p className="text-sm text-gray-500">Browse all interactions with search and filtering.</p>
        </Link>
        <Link to="/agent" className="card-hover p-5 block">
          <div className="flex items-center space-x-3 mb-2">
            <UserCheck size={20} className="text-primary-600" />
            <h3 className="font-semibold text-gray-900">Agent Performance</h3>
          </div>
          <p className="text-sm text-gray-500">View agent leaderboard, scores, and detailed performance.</p>
        </Link>
        <Link to="/coaching" className="card-hover p-5 block">
          <div className="flex items-center space-x-3 mb-2">
            <GraduationCap size={20} className="text-primary-600" />
            <h3 className="font-semibold text-gray-900">Coaching & QA</h3>
          </div>
          <p className="text-sm text-gray-500">Review agent scorecards, QA results, and coaching notes.</p>
        </Link>
      </div>

      {/* Trend Charts */}
      {trendData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <TrendChart
            title="Daily Volume"
            ariaLabel="Daily conversation volume showing calls and emails"
            data={trendData}
            xKey="date"
            height={220}
            lines={[
              { dataKey: 'calls', color: '#3b82f6', label: 'Calls' },
              { dataKey: 'emails', color: '#f59e0b', label: 'Emails' },
            ]}
          />
          <TrendChart
            title="Sentiment Trend"
            ariaLabel="Average sentiment score trend over the selected period"
            data={trendData.map(d => ({ ...d, sentimentPct: sentimentToPercent(d.sentiment) }))}
            xKey="date"
            height={220}
            lines={[
              { dataKey: 'sentimentPct', color: '#22c55e', label: 'Sentiment %' },
            ]}
          />
        </div>
      )}

      {/* Agents Needing Attention + Root Cause Analysis + Dispositions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Agents Needing Attention */}
        {attentionAgents.length > 0 && (
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700">Agents Needing Attention</h3>
              <Link to="/agent" className="text-xs text-primary-600 hover:text-primary-800">View all</Link>
            </div>
            <div className="space-y-3">
              {attentionAgents.map(agent => {
                const sentimentPct = sentimentToPercent(agent.avg_sentiment);
                const barColor = sentimentPct >= 70 ? 'bg-green-500' : sentimentPct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
                return (
                  <Link
                    key={agent.id || agent.agent_id}
                    to={`/agent/${agent.id || agent.agent_id}`}
                    className="flex items-center justify-between py-2 hover:bg-gray-50 rounded-lg px-2 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{agent.name || agent.agent_id}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[120px]">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${sentimentPct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500">{sentimentToPercent(agent.avg_sentiment)}%</span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-xs text-gray-500">{agent.total_interactions ?? 0} interactions</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Root Cause Analysis */}
        {contactReasons.length > 0 && (
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Why Customers Are Calling</h3>
            <div className="space-y-3">
              {contactReasons.slice(0, 8).map(r => (
                <div key={r.reason} className="flex items-center space-x-3">
                  <span className="text-sm text-gray-700 w-36 truncate">{toTitleCase(r.reason)}</span>
                  <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full"
                      style={{ width: `${r.percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-16 text-right">{r.count} ({r.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Disposition/Outcome Summary */}
        <DistributionChart
          title="Conversation Outcomes"
          centerLabel="outcomes"
          data={dispositions.map(d => ({
            name: toTitleCase(d.disposition),
            value: d.count,
            color: DISPOSITION_COLORS[d.disposition] || '#9ca3af',
          }))}
        />
      </div>
    </div>
  );
};

export default DashboardPage;
